/**
 * @file        fetch-keystore.js
 *              Utility code to fetch keystores from the network.
 * @author      Wes Garland, wes@kingsds.network
 * @date        Aug 2021
 */

'use strict';

const debugging = require('dcp/debugging').scope('session');
const { DCPError } = require('dcp/common/dcp-error');

/**
 * Fetch a keystore from a portal server
 *
 * @param {DcpURL|URL} URL to fetch
 * @param {object} options
 *   - onError:     callback to invoke when the fetch fails or the remote returns not { status: true }.
 *                  The callback will receive an error object and maybe a suggested URL to redirect the
 *                  user to.
 *   - oAuthUnlock: use the oAuthToken to proactively unlock the keystore
 */
exports.fetchKeystore = async function fetchKeystore(url, options)
{
  const { justFetch } = require('dcp/utils');
  var response, keystore;
  var onErrorRedirect;

  try
  {
    debugging() && console.debug('fetching keystore from', url.href);
    response = await justFetch(url, 'JSON');

    if (response.success !== true) {
      let error;

      if (!response.error)
      {
        console.error('Invalid response fetching identity from portal:', response);
        throw new DCPError(`Invalid response fetching identity from portal (success=${response.success} and missing error)`, 'DCPK-1001');
      }
      error = new DCPError(`${response.error.message} fetching keystore from ${url.href}`);
      if (response.error.stack && error.stack)
        error.stack = '\n----------\n' + response.error.stack;
      error.code  = 'REMOTE_' + response.error.code;

      if (response.redirect)
        onErrorRedirect = response.redirect;

      throw error;
    }

    if (options.onLoad)
      options.onLoad(response);
    keystore = await (new options.KeystoreConstructor(response.keystore));
    if (options.onReady)
      options.onReady(keystore);
  }
  catch(error)
  {
    if (options.onError)
      options.onError(error, onErrorRedirect);

    return null;
  }

  return keystore;
}
